import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../auth/auth.service';
import { sanitizeInternalReturnUrl } from '../auth/internal-return-url';

export const authGuard: CanActivateFn = async (_route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Password recovery link: Supabase may redirect to site root with hash. Send to reset-password.
  const hash = typeof window !== 'undefined' ? window.location.hash : '';
  if (hash.includes('type=recovery')) {
    const fragment = hash.startsWith('#') ? hash.slice(1) : hash;
    router.navigate(['/reset-password'], { replaceUrl: true, fragment });
    return false;
  }

  await authService.waitForInitialization();

  if (authService.isAuthenticated()) {
    try {
      if (await authService.requiresMfaEnrollment()) {
        const isMfaSetupRoute = state.url.startsWith('/settings');
        if (!isMfaSetupRoute) {
          router.navigate(['/settings'], { queryParams: { mfa: 'setup' } });
          return false;
        }
        await authService.ensureUserDataLoaded();
        if (!(await authService.enforceActiveAccountOrSignOut())) {
          return false;
        }
        return true;
      }

      if (await authService.requiresMfaVerification()) {
        router.navigate(['/login'], { queryParams: { mfa: 'required' } });
        return false;
      }
    } catch (error) {
      console.error('Failed to evaluate MFA guard state:', error);
      router.navigate(['/login']);
      return false;
    }
    await authService.ensureUserDataLoaded();
    if (!(await authService.enforceActiveAccountOrSignOut())) {
      return false;
    }
    return true;
  }

  const returnUrl = sanitizeInternalReturnUrl(state.url);
  router.navigate(['/login'], returnUrl ? { queryParams: { returnUrl } } : undefined);
  return false;
};

